<template>
  <div class="sale-builder">
    <section class="fade-in" v-if="!initialLoading">
      <div class="card-block">
        <div class="block-header">
          <div>
            <h2>Articles de la vente</h2>
            <p>Modifiez les articles de la vente.</p>
          </div>
          <div class="block-actions">
            <button
              class="btn btn-outline"
              @click="refreshAll"
              :disabled="refreshing || itemSubmitting"
            >
              <i class="bi bi-arrow-clockwise"></i>
              <span>Actualiser</span>
            </button>
          </div>
        </div>

        <!-- Search Section -->
        <div class="search-section">
          <div class="search-mode-selector">
            <button
              type="button"
              class="search-mode-btn"
              :class="{ active: searchMode === 'barcode' }"
              @click="setSearchMode('barcode')"
            >
              <i class="bi bi-upc-scan"></i>
              Code-barres
            </button>
            <button
              type="button"
              class="search-mode-btn"
              :class="{ active: searchMode === 'code' }"
              @click="setSearchMode('code')"
            >
              <i class="bi bi-search"></i>
              Code / Nom
            </button>
          </div>

          <!-- Barcode Search -->
          <form
            v-if="searchMode === 'barcode'"
            class="search-form"
            @submit.prevent="searchAndAddItemByBarcode"
          >
            <div class="search-box">
              <i class="bi bi-upc-scan"></i>
              <input
                ref="barcodeInput"
                v-model="barcodeSearch"
                type="text"
                placeholder="Scanner ou saisir un code-barres..."
                @input="onBarcodeInput"
              />
            </div>
            <button
              type="submit"
              class="search-submit-btn"
              :disabled="!barcodeSearch || searchingItem"
            >
              <i v-if="!searchingItem" class="bi bi-search"></i>
              <span v-else class="spinner-small"></span>
              Rechercher
            </button>
          </form>

          <!-- Search by Code or Name -->
          <div v-if="searchMode === 'code'" class="search-form">
            <div class="search-box">
              <i class="bi bi-search"></i>
              <input
                ref="itemSearchInput"
                v-model="itemSearchTerm"
                type="text"
                placeholder="Rechercher par code article ou nom..."
                @input="onItemSearchInput"
              />
              <span v-if="searchingItems" class="spinner-small"></span>
            </div>
          </div>

          <!-- Search Results -->
          <div
            v-if="itemSearchResults.length > 0"
            class="search-results-section"
          >
            <h4 class="search-results-title">
              Résultats de recherche ({{ itemSearchResults.length }})
            </h4>
            <div class="search-results-grid">
              <div
                v-for="item in itemSearchResults"
                :key="item.reference || item.id"
                class="search-result-card"
                @click="addItemFromSearch(item)"
              >
                <div class="result-image-wrapper">
                  <img
                    v-if="getImagePath(item)"
                    :src="$imageUrl(getImagePath(item))"
                    :alt="item.fullName || item.name"
                    @error="handleImageError($event)"
                  />
                  <div v-else class="item-image-placeholder">
                    <i class="bi bi-image"></i>
                  </div>
                </div>
                <div class="result-info">
                  <h5 class="result-name">
                    {{ item.fullName || item.name || "Article" }}
                  </h5>
                  <p class="result-code" v-if="item.reference">
                    Code: {{ item.reference }}
                  </p>
                  <div class="result-price">
                    {{ formatAmount(getProductPrice(item)) }} MAD
                  </div>
                </div>
                <div class="result-action">
                  <button class="add-item-btn">
                    <i class="bi bi-plus-circle"></i>
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="product-selection">
          <div v-if="itemsLoading" class="product-state">
            <div class="loader"></div>
            <p>Chargement de la liste des produits…</p>
          </div>
          <div v-else-if="!productOptions.length" class="product-state">
            <i class="bi bi-box-seam"></i>
            <p>Aucun produit disponible pour le moment.</p>
          </div>
          <div v-else class="product-grid">
            <button
              v-for="product in productOptions"
              :key="product.reference || product.id || product.uuid"
              type="button"
              class="product-card"
              @click="openProductModal(product)"
              :disabled="itemSubmitting"
            >
              <div class="product-image-wrapper">
                <img
                  v-if="getImagePath(product)"
                  :src="$imageUrl(getImagePath(product))"
                  :alt="product.fullName"
                  @error="handleImageError($event)"
                />
                <div v-else class="product-placeholder">
                  <i class="bi bi-image"></i>
                </div>
              </div>
              <div class="product-card-body">
                <h4>{{ product.fullName }}</h4>
                <span class="product-price">
                  {{ formatAmount(getProductPrice(product)) }}
                </span>
                <span
                  v-if="getProductStock(product) !== null"
                  class="product-stock"
                >
                  Stock: {{ formatQuantity(getProductStock(product)) }} KG
                </span>
              </div>
            </button>
          </div>
        </div>

        <div class="items-cards-wrapper">
          <div v-if="itemsLoading" class="items-state">
            <div class="loader"></div>
            <p>Chargement des articles…</p>
          </div>
          <div v-else-if="!preparedSaleItems.length" class="items-state">
            <i class="bi bi-archive"></i>
            <p>Aucun article ajouté pour le moment.</p>
          </div>
          <div v-else class="items-cards-grid">
            <div
              v-for="line in preparedSaleItems"
              :key="line.id"
              class="item-card"
            >
              <div class="item-card-header">
                <div class="item-card-title">
                  <h4>{{ line.designation }}</h4>
                  <small v-if="line.reference" class="item-reference"
                    >Réf. {{ line.reference }}</small
                  >
                </div>
                <button
                  class="btn btn-danger btn-sm"
                  @click="deleteItem(line.raw)"
                  :disabled="itemSubmitting"
                  title="Supprimer"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
              <div class="item-card-body">
                <div class="item-info-row">
                  <span class="item-label">Prix unitaire</span>
                  <span class="item-value badge badge-light">{{
                    formatAmount(line.price)
                  }}</span>
                </div>
                <div class="item-info-row">
                  <span class="item-label">Quantité</span>
                  <span class="item-value badge badge-dark">{{
                    formatQuantity(line.quantity)
                  }}</span>
                </div>
                <div class="item-info-row">
                  <span class="item-label">Total TTC</span>
                  <span class="item-value badge badge-primary">{{
                    formatAmount(line.total)
                  }}</span>
                </div>
                <div v-if="line.remark" class="item-remark">
                  <i class="bi bi-chat-left-text"></i>
                  <span>{{ line.remark }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="preparedSaleItems.length > 0" class="items-total-card">
            <div class="total-label">Total général</div>
            <div class="total-amount">{{ formatAmount(totalAmount) }}</div>
          </div>
        </div>
      </div>

      <div
        v-if="showProductModal && activeProduct"
        class="product-modal-overlay"
        @click="closeProductModal"
      >
        <div class="product-modal" @click.stop>
          <header class="product-modal-header">
            <div>
              <h3>{{ activeProduct.fullName }}</h3>
              <p>
                Prix TTC : {{ formatAmount(getProductPrice(activeProduct)) }}
                <span
                  v-if="getProductStock(activeProduct) !== null"
                  class="stock-info"
                >
                  • Stock disponible :
                  {{ formatQuantity(getProductStock(activeProduct)) }} KG
                </span>
              </p>
            </div>
            <button class="btn btn-light icon-only" @click="closeProductModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </header>

          <section class="product-modal-body">
            <div v-if="getImagePath(activeProduct)" class="product-modal-image">
              <img
                :src="$imageUrl(getImagePath(activeProduct))"
                :alt="activeProduct.fullName"
                @error="handleImageError($event)"
              />
            </div>

            <div class="quantity-panels">
              <div class="quantity-display">
                <div class="quantity-display-header">
                  <span>Quantité (KG)</span>
                  <button
                    v-if="modalQuantity"
                    type="button"
                    class="btn-reset"
                    @click="resetQuantity"
                    title="Réinitialiser la quantité"
                  >
                    <i class="bi bi-arrow-counterclockwise"></i>
                  </button>
                </div>
                <strong>{{ modalQuantity || "0" }}</strong>
              </div>
              <div class="quantity-display secondary">
                <span>Total TTC</span>
                <strong>{{ formatAmount(modalTotalAmount) }}</strong>
              </div>
            </div>

            <div class="quantity-keyboard">
              <button
                v-for="key in keypadLayout"
                :key="key.label"
                type="button"
                class="btn btn-keyboard"
                :class="{
                  danger: key.type === 'clear',
                  action: key.type === 'submit',
                }"
                @click="handleKeypadPress(key)"
                :disabled="itemSubmitting && key.type === 'submit'"
              >
                {{ key.label }}
              </button>
            </div>

            <div class="remark-group">
              <label for="modal-remark">Remarque (optionnel)</label>
              <textarea
                id="modal-remark"
                v-model.trim="modalRemark"
                rows="2"
                placeholder="Ajouter une note pour cet article…"
              ></textarea>
            </div>
          </section>

          <footer class="product-modal-footer">
            <button
              class="btn btn-light"
              type="button"
              @click="closeProductModal"
            >
              Annuler
            </button>
            <button
              class="btn btn-success"
              type="button"
              :disabled="!canConfirmModal || itemSubmitting"
              @click="confirmProductSelection"
            >
              <i class="bi bi-check-circle"></i>
              <span v-if="!itemSubmitting">Ajouter</span>
              <span v-else>Ajout…</span>
            </button>
          </footer>
        </div>
      </div>

      <div
        v-if="showCustomerModal"
        class="customer-modal-overlay"
        @click="closeCustomerModal"
      >
        <div class="customer-modal" @click.stop>
          <header class="customer-modal-header">
            <div>
              <h3>Sélectionner un client</h3>
              <p>Choisissez le client pour cette vente</p>
            </div>
            <button class="btn btn-light icon-only" @click="closeCustomerModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </header>

          <section class="customer-modal-body">
            <div class="customer-search">
              <i class="bi bi-search"></i>
              <input
                v-model="customerSearchTerm"
                type="search"
                placeholder="Rechercher un client (nom, téléphone, ville...)"
              />
            </div>

            <div v-if="filteredCustomers.length === 0" class="customer-state">
              <i class="bi bi-people"></i>
              <p>Aucun client trouvé</p>
            </div>

            <div v-else class="customer-list">
              <button
                v-for="customer in filteredCustomers"
                :key="customer.reference || customer.id"
                type="button"
                class="customer-item"
                @click="selectCustomer(customer)"
              >
                <div class="customer-item-content">
                  <div class="customer-avatar">
                    {{ getCustomerInitials(customer) }}
                  </div>
                  <div class="customer-details">
                    <h4>
                      {{
                        customer.fullName ||
                        customer.name ||
                        customer.society ||
                        customer.reference
                      }}
                    </h4>
                    <div class="customer-meta">
                      <span v-if="customer.society" class="meta-item">
                        <i class="bi bi-building"></i>
                        {{ customer.society }}
                      </span>
                      <span v-if="customer.phone" class="meta-item">
                        <i class="bi bi-telephone"></i>
                        {{ customer.phone }}
                      </span>
                      <span
                        v-if="customer.city || customer.address"
                        class="meta-item"
                      >
                        <i class="bi bi-geo-alt"></i>
                        {{ customer.city || customer.address }}
                      </span>
                    </div>
                  </div>
                </div>
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </section>
        </div>
      </div>

      <div class="card-block">
        <h2>Détails de la vente</h2>
        <p class="block-subtitle">
          Renseignez le client et les informations principales. Les montants
          sont calculés automatiquement.
        </p>

        <form class="sale-form" @submit.prevent="saveSale">
          <div class="form-group">
            <label>Client *</label>
            <button
              type="button"
              class="form-control customer-select-btn"
              :class="{ 'has-error': formErrors.customer_reference }"
              @click="openCustomerModal"
            >
              <span v-if="selectedCustomer">
                <i class="bi bi-person"></i>
                {{
                  selectedCustomer.fullName ||
                  selectedCustomer.name ||
                  selectedCustomer.society ||
                  selectedCustomer.reference
                }}
              </span>
              <span v-else class="placeholder">
                <i class="bi bi-person-plus"></i>
                Choisir un client
              </span>
              <i class="bi bi-chevron-down"></i>
            </button>
            <p v-if="formErrors.customer_reference" class="error">
              {{ formErrors.customer_reference }}
            </p>
          </div>

          <div class="form-group">
            <label>Référence *</label>
            <input
              v-model.trim="form.reference"
              type="text"
              class="form-control"
              :class="{ 'has-error': formErrors.reference }"
              placeholder="SALE-0001"
            />
            <p v-if="formErrors.reference" class="error">
              {{ formErrors.reference }}
            </p>
          </div>

          <div class="form-group">
            <label>Date *</label>
            <input
              v-model="form.date"
              type="date"
              class="form-control"
              :class="{ 'has-error': formErrors.date }"
            />
            <p v-if="formErrors.date" class="error">{{ formErrors.date }}</p>
          </div>

          <div class="form-group full">
            <label>Remarque</label>
            <textarea
              v-model.trim="form.remark"
              rows="3"
              class="form-control"
              placeholder="Instructions complémentaires, remarques…"
            ></textarea>
          </div>

          <div class="summary-card">
            <div>
              <span>Total TTC</span>
              <strong>{{ formatAmount(totalAmount) }}</strong>
            </div>
            <div>
              <span>Nombre d'articles</span>
              <strong>{{ preparedSaleItems.length }}</strong>
            </div>
          </div>

          <p v-if="formErrors.items" class="error form-error-inline">
            {{ formErrors.items }}
          </p>

          <div class="form-actions">
            <button
              type="button"
              class="btn btn-light"
              @click="goBack"
              :disabled="submitting"
            >
              Annuler
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="submitting || !canSubmit"
            >
              <i class="bi bi-check-circle"></i>
              <span v-if="!submitting">Enregistrer</span>
              <span v-else>Enregistrement…</span>
            </button>
          </div>
        </form>
      </div>

      <p class="hint">
        <span class="text-danger">*</span> Champ obligatoire &nbsp;•&nbsp; Le
        montant total est calculé à partir des articles ajoutés.
      </p>
    </section>

    <section class="loading-state" v-else>
      <div class="loader"></div>
      <p>Chargement de la vente…</p>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "SaleEdit",
  data() {
    return {
      initialLoading: true,
      refreshing: false,
      itemsLoading: false,
      submitting: false,
      itemSubmitting: false,
      showProductModal: false,
      activeProduct: null,
      modalQuantity: "",
      modalRemark: "",
      showCustomerModal: false,
      customerSearchTerm: "",
      searchMode: "barcode", // 'barcode' or 'code'
      barcodeSearch: "",
      itemSearchTerm: "",
      searchingItem: false,
      searchingItems: false,
      itemSearchResults: [],
      searchTimeout: null,
      imageFallback:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23e2e8f0'/%3E%3Cg fill='%2394a3b8' font-family='sans-serif' font-size='12'%3E%3Ctext x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'%3ENo Image%3C/text%3E%3C/g%3E%3C/svg%3E",
      form: {
        reference: "",
        customer_reference: "",
        date: "",
        total_amount: 0,
        remark: "",
      },
      formErrors: {},
    };
  },
  computed: {
    ...mapGetters("sale", {
      sale: "getSale",
    }),
    ...mapGetters("sale", {
      saleItems: "getItems",
    }),
    ...mapGetters("customer", {
      customers: "getAll",
    }),
    ...mapGetters("cart", {
      cartItems: "getItems",
    }),
    reference() {
      return this.$route.params.reference;
    },
    productOptions() {
      let source = [];
      if (Array.isArray(this.saleItems)) {
        source = this.saleItems;
      } else if (
        this.saleItems &&
        typeof this.saleItems === "object" &&
        Array.isArray(this.saleItems.data)
      ) {
        source = this.saleItems.data;
      }

      return source.map((entry) => {
        const itemData =
          (entry && typeof entry === "object" && entry.item) || {};
        const reference =
          entry?.item_reference != null
            ? entry?.item_reference
            : itemData?.reference != null
            ? itemData?.reference
            : itemData?.item_reference != null
            ? itemData?.item_reference
            : entry?.reference != null
            ? entry?.reference
            : "";
        const fullName =
          entry?.fullName != null
            ? entry?.fullName
            : itemData?.fullName != null
            ? itemData?.fullName
            : itemData?.name != null
            ? itemData?.name
            : itemData?.designation != null
            ? itemData?.designation
            : reference;
        const price =
          entry?.priceTTC != null
            ? entry?.priceTTC
            : entry?.price_ttc != null
            ? entry?.price_ttc
            : itemData?.priceTTC != null
            ? itemData?.priceTTC
            : itemData?.price_ttc != null
            ? itemData?.price_ttc
            : itemData?.price != null
            ? itemData?.price
            : itemData?.unit_price != null
            ? itemData?.unit_price
            : null;
        const pcsCandidate =
          entry?.pcs != null
            ? entry?.pcs
            : itemData?.pcs != null
            ? itemData?.pcs
            : itemData?.items_per_pack != null
            ? itemData?.items_per_pack
            : itemData?.packaging != null
            ? itemData?.packaging
            : null;
        const normalizedPcs =
          pcsCandidate === null || pcsCandidate === undefined
            ? null
            : Number.isFinite(Number(pcsCandidate))
            ? Number(pcsCandidate)
            : pcsCandidate;
        const image =
          entry?.image != null
            ? entry?.image
            : entry?.item_image != null
            ? entry?.item_image
            : itemData?.image != null
            ? itemData?.image
            : itemData?.image_url != null
            ? itemData?.image_url
            : itemData?.photo != null
            ? itemData?.photo
            : itemData?.picture != null
            ? itemData?.picture
            : null;
        const stockQuantity =
          entry?.stockQuantity != null
            ? entry?.stockQuantity
            : entry?.stock_quantity != null
            ? entry?.stock_quantity
            : entry?.quantity != null
            ? entry?.quantity
            : entry?.qty != null
            ? entry?.qty
            : itemData?.stockQuantity != null
            ? itemData?.stockQuantity
            : itemData?.stock_quantity != null
            ? itemData?.stock_quantity
            : itemData?.quantity != null
            ? itemData?.quantity
            : itemData?.qty != null
            ? itemData?.qty
            : itemData?.stock != null
            ? itemData?.stock
            : null;

        return {
          ...entry,
          reference,
          fullName,
          priceTTC: price,
          pcs: normalizedPcs,
          image,
          stockQuantity,
          item: itemData,
          rawSaleItem: entry,
        };
      });
    },
    preparedSaleItems() {
      return (this.cartItems || []).map((item) => {
        const quantity = Number(
          item.quantity != null
            ? item.quantity
            : item.qty != null
            ? item.qty
            : 0
        );
        const price = Number(
          item.priceTTC != null
            ? item.priceTTC
            : item.price_ttc != null
            ? item.price_ttc
            : (item.item && item.item.priceTTC) != null
            ? item.item.priceTTC
            : (item.item && item.item.price_ttc) != null
            ? item.item.price_ttc
            : item.unit_price != null
            ? item.unit_price
            : 0
        );
        const total = Number(
          item.totalPriceTTC != null
            ? item.totalPriceTTC
            : item.total_price_ttc != null
            ? item.total_price_ttc
            : price * quantity
        );
        return {
          id:
            item.id ||
            item.uuid ||
            item.item_reference ||
            `${item.item_reference || item.reference}-${Date.now()}`,
          reference: item.item_reference || item.reference,
          designation:
            item.itemFullName ||
            item.item_full_name ||
            item.item?.fullName ||
            item.item?.name ||
            item.designation ||
            item.item_reference ||
            "Article",
          price,
          quantity,
          total,
          remark: item.remark || item.note || "",
          raw: item,
        };
      });
    },
    totalAmount() {
      return this.preparedSaleItems.reduce(
        (sum, item) => sum + Number(item.total || 0),
        0
      );
    },
    canSubmit() {
      return (
        !!this.form.customer_reference &&
        !!this.form.reference &&
        !!this.form.date &&
        this.preparedSaleItems.length > 0
      );
    },
    modalTotalAmount() {
      if (!this.activeProduct) return 0;
      const quantity = Number(this.modalQuantity);
      const price = Number(this.getProductPrice(this.activeProduct) || 0);
      const total = quantity * price;
      return Number.isFinite(total) ? total : 0;
    },
    canConfirmModal() {
      const quantity = Number(this.modalQuantity);
      return (
        !!this.activeProduct &&
        !Number.isNaN(quantity) &&
        quantity > 0 &&
        Number.isFinite(quantity)
      );
    },
    keypadLayout() {
      return [
        { label: "7", value: "7" },
        { label: "8", value: "8" },
        { label: "9", value: "9" },
        { label: "⌫", type: "backspace" },
        { label: "4", value: "4" },
        { label: "5", value: "5" },
        { label: "6", value: "6" },
        { label: "C", type: "clear" },
        { label: "1", value: "1" },
        { label: "2", value: "2" },
        { label: "3", value: "3" },
        { label: "0", value: "0" },
        { label: "00", value: "00" },
        { label: ".", value: "." },
      ];
    },
    selectedCustomer() {
      if (!this.form.customer_reference) return null;
      return (
        (this.customers || []).find(
          (c) => c.reference === this.form.customer_reference
        ) || null
      );
    },
    filteredCustomers() {
      if (!this.customerSearchTerm) return this.customers || [];
      const term = this.customerSearchTerm.toLowerCase();
      return (this.customers || []).filter((customer) =>
        [
          customer.fullName,
          customer.name,
          customer.society,
          customer.phone,
          customer.email,
          customer.city,
          customer.address,
          customer.reference,
        ]
          .filter(Boolean)
          .map((value) => value.toString().toLowerCase())
          .some((value) => value.includes(term))
      );
    },
  },
  watch: {
    sale: {
      handler(newValue) {
        if (!newValue || Object.keys(newValue).length === 0) return;
        this.form = {
          reference: newValue.reference || "",
          customer_reference:
            newValue.customer_reference || newValue.customer_reference || "",
          date: newValue.date || newValue.sale_date || "",
          total_amount:
            newValue.total_amount ||
            newValue.totalAmount ||
            newValue.total_price_ttc ||
            newValue.totalPriceTTC ||
            0,
          remark: newValue.remark || newValue.notes || "",
        };
        // Load sale items into cart
        this.loadSaleItemsIntoCart(newValue);
      },
      immediate: true,
      deep: true,
    },
    totalAmount: {
      immediate: true,
      handler(value) {
        this.form.total_amount = Number(
          value.toFixed ? value.toFixed(2) : value
        );
      },
    },
  },
  methods: {
    setSearchMode(mode) {
      this.searchMode = mode;
      this.$nextTick(() => {
        if (mode === "barcode" && this.$refs.barcodeInput) {
          this.$refs.barcodeInput.focus();
        } else if (mode === "code" && this.$refs.itemSearchInput) {
          this.$refs.itemSearchInput.focus();
        }
      });
    },
    onBarcodeInput() {
      // Keep input focused
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) {
          this.$refs.barcodeInput.focus();
        }
      });
    },
    onItemSearchInput() {
      // Clear results when search term is empty
      if (!this.itemSearchTerm || this.itemSearchTerm.trim().length === 0) {
        this.itemSearchResults = [];
        return;
      }
      // Debounce search - search after user stops typing
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.searchItems();
      }, 300); // Wait 300ms after user stops typing
    },
    async searchItems() {
      if (!this.itemSearchTerm || !this.itemSearchTerm.trim()) {
        return;
      }

      this.searchingItems = true;
      this.itemSearchResults = [];
      try {
        const response = await this.$api.instance.get("/api/items", {
          params: {
            search: this.itemSearchTerm.trim(),
            per_page: 20,
          },
        });

        if (response.data) {
          let items = [];
          if (response.data.data && Array.isArray(response.data.data)) {
            items = response.data.data;
          } else if (Array.isArray(response.data)) {
            items = response.data;
          }

          const searchTerm = this.itemSearchTerm.toLowerCase().trim();
          this.itemSearchResults = items.filter((item) => {
            const code = (item.reference || "").toLowerCase();
            const fullName = (item.fullName || item.name || "").toLowerCase();
            return code.includes(searchTerm) || fullName.includes(searchTerm);
          });
        }
      } catch (error) {
        console.error("Error searching items:", error);
        this.$toast?.error?.("Erreur lors de la recherche d'articles");
      } finally {
        this.searchingItems = false;
      }
    },
    async searchAndAddItemByBarcode() {
      if (!this.barcodeSearch || !this.barcodeSearch.trim()) {
        return;
      }

      this.searchingItem = true;
      try {
        const response = await this.$api.instance.get(
          "/api/items/find/search",
          {
            params: {
              search: this.barcodeSearch.trim(),
              barcode_only: true,
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data
        ) {
          const item = response.data.data;
          await this.addItemFromSearch(item);
          this.barcodeSearch = "";
          this.$nextTick(() => {
            if (this.$refs.barcodeInput) {
              this.$refs.barcodeInput.focus();
            }
          });
        } else {
          this.$toast?.error?.("Article non trouvé");
        }
      } catch (error) {
        console.error("Error searching item:", error);
        const message =
          error.response?.data?.message ||
          "Erreur lors de la recherche de l'article";
        this.$toast?.error?.(message);
      } finally {
        this.searchingItem = false;
        this.barcodeSearch = "";
        this.$nextTick(() => {
          if (this.$refs.barcodeInput) {
            this.$refs.barcodeInput.focus();
          }
        });
      }
    },
    async addItemFromSearch(item) {
      const price = this.getProductPrice(item);
      const itemPayload = {
        item_reference: item.reference || "",
        quantity: 1,
        priceTTC: price,
        remark: "",
      };
      if (!itemPayload.item_reference) {
        this.$toast?.error?.("Référence produit introuvable.");
        return;
      }
      const saved = await this.saveItem(itemPayload);
      if (saved) {
        this.itemSearchResults = [];
        this.itemSearchTerm = "";
        this.barcodeSearch = "";
      }
    },
    loadSaleItemsIntoCart(saleData) {
      const items = Array.isArray(saleData.sale_items)
        ? saleData.sale_items
        : Array.isArray(saleData.items)
        ? saleData.items
        : [];
      // Set items in cart
      this.$store.commit("cart/setItems", items);
    },
    openProductModal(product) {
      if (!product) return;
      this.activeProduct = product;
      this.modalQuantity = "";
      this.modalRemark = "";
      this.showProductModal = true;
    },
    closeProductModal() {
      if (this.itemSubmitting) return;
      this.showProductModal = false;
      this.activeProduct = null;
      this.modalQuantity = "";
      this.modalRemark = "";
    },
    goBack() {
      this.$router.back();
    },
    openCustomerModal() {
      this.customerSearchTerm = "";
      this.showCustomerModal = true;
    },
    closeCustomerModal() {
      this.showCustomerModal = false;
      this.customerSearchTerm = "";
    },
    selectCustomer(customer) {
      if (!customer || !customer.reference) return;
      this.form.customer_reference = customer.reference;
      this.closeCustomerModal();
    },
    getCustomerInitials(customer) {
      const name = customer.fullName || customer.name || customer.society || "";
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      const initials = [parts[0], parts[parts.length - 1]]
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase())
        .join("");
      return initials || name.charAt(0).toUpperCase();
    },
    getImagePath(product) {
      if (!product) {
        return "";
      }
      return product.image != null
        ? product.image
        : product.item?.image != null
        ? product.item?.image
        : product.item?.image_url != null
        ? product.item?.image_url
        : product.item?.photo != null
        ? product.item?.photo
        : "";
    },
    handleImageError(event) {
      if (event && event.target) {
        event.target.src = this.imageFallback;
      }
    },
    getProductPrice(product) {
      if (!product) return 0;
      const price =
        product.priceTTC != null
          ? product.priceTTC
          : product.price_ttc != null
          ? product.price_ttc
          : product.item?.priceTTC != null
          ? product.item?.priceTTC
          : product.item?.price_ttc != null
          ? product.item?.price_ttc
          : product.price != null
          ? product.price
          : product.unit_price != null
          ? product.unit_price
          : 0;
      return Number(price) || 0;
    },
    getProductStock(product) {
      if (!product) return null;
      const stock =
        product.stockQuantity != null
          ? product.stockQuantity
          : product.stock_quantity != null
          ? product.stock_quantity
          : product.quantity != null
          ? product.quantity
          : product.qty != null
          ? product.qty
          : product.stock != null
          ? product.stock
          : product.item?.stockQuantity != null
          ? product.item?.stockQuantity
          : product.item?.stock_quantity != null
          ? product.item?.stock_quantity
          : product.item?.quantity != null
          ? product.item?.quantity
          : product.item?.qty != null
          ? product.item?.qty
          : product.item?.stock != null
          ? product.item?.stock
          : product.rawSaleItem?.quantity != null
          ? product.rawSaleItem?.quantity
          : product.rawSaleItem?.qty != null
          ? product.rawSaleItem?.qty
          : product.rawSaleItem?.stock != null
          ? product.rawSaleItem?.stock
          : null;
      return stock !== null ? Number(stock) : null;
    },
    resetQuantity() {
      this.modalQuantity = "";
    },
    formatAmount(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      return number.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatQuantity(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      const hasDecimals = number % 1 !== 0;
      return hasDecimals ? number.toFixed(2) : number.toString();
    },
    handleKeypadPress(key) {
      if (!key) return;
      if (key.type === "clear") {
        this.modalQuantity = "";
        return;
      }
      if (key.type === "backspace") {
        this.modalQuantity = this.modalQuantity.slice(
          0,
          Math.max(0, this.modalQuantity.length - 1)
        );
        return;
      }
      const value = key.value;
      if (value === undefined) {
        return;
      }
      if (value === ".") {
        if (this.modalQuantity.includes(".")) {
          return;
        }
        this.modalQuantity = this.modalQuantity
          ? `${this.modalQuantity}.`
          : "0.";
        return;
      }
      if (value === "00") {
        if (!this.modalQuantity) return;
        this.modalQuantity += "00";
        return;
      }
      if (this.modalQuantity === "0") {
        this.modalQuantity = value;
      } else {
        this.modalQuantity += value;
      }
    },
    async confirmProductSelection() {
      if (!this.activeProduct) return;
      const quantity = Number(this.modalQuantity);
      if (!this.canConfirmModal || Number.isNaN(quantity)) {
        return;
      }
      const price = this.getProductPrice(this.activeProduct);
      const numericPrice = Number(price);
      const itemPayload = {
        item_reference: this.activeProduct.reference || "",
        quantity: quantity,
        priceTTC: Number.isNaN(numericPrice) ? price : numericPrice.toFixed(2),
        remark: this.modalRemark || "",
      };
      if (!itemPayload.item_reference) {
        this.$toast?.error?.("Référence produit introuvable.");
        return;
      }
      const saved = await this.saveItem(itemPayload);
      if (saved) {
        this.closeProductModal();
      }
    },
    async loadInitialData() {
      this.initialLoading = true;
      try {
        this.itemsLoading = true;
        await Promise.all([
          this.$store.dispatch("sale/show", this.reference),
          this.$store.dispatch("sale/getItems"),
          this.customers && this.customers.length
            ? Promise.resolve()
            : this.$store.dispatch("customer/getAll"),
        ]);
      } catch (error) {
        console.error("Error preparing sale edit", error);
        this.$toast?.error?.("Impossible de charger la vente.");
      } finally {
        this.itemsLoading = false;
        this.initialLoading = false;
      }
    },
    async refreshAll() {
      this.refreshing = true;
      try {
        this.itemsLoading = true;
        await Promise.all([
          this.$store.dispatch("sale/getItems"),
          this.$store.dispatch("cart/getItems"),
        ]);
        this.$toast?.success?.("Liste des articles mise à jour.");
      } catch (error) {
        console.error("Error refreshing lists", error);
        this.$toast?.error?.("Impossible d'actualiser les listes.");
      } finally {
        this.itemsLoading = false;
        this.refreshing = false;
      }
    },
    async saveItem(itemPayload) {
      if (this.itemSubmitting) return false;
      this.itemSubmitting = true;
      let success = false;
      try {
        await this.$store.dispatch("sale/storeItem", itemPayload);
        await this.$store.dispatch("cart/getItems");
        this.$toast?.success?.("Article ajouté à la vente.");
        success = true;
      } catch (error) {
        console.error("Error saving sale item", error);
        this.$toast?.error?.("Impossible d'ajouter l'article.");
      } finally {
        this.itemSubmitting = false;
      }
      return success;
    },
    async deleteItem(item) {
      if (!item) return;
      const reference = item.id || item.reference || item.item_reference;
      if (!reference) return;
      this.itemSubmitting = true;
      try {
        await this.$store.dispatch("cart/destroyItem", reference);
        await this.$store.dispatch("cart/getItems");
        this.$toast?.success?.("Article supprimé.");
      } catch (error) {
        console.error("Error deleting sale item", error);
        this.$toast?.error?.("Impossible de supprimer l'article.");
      } finally {
        this.itemSubmitting = false;
      }
    },
    validateForm() {
      const errors = {};

      if (!this.form.reference) {
        errors.reference = "La référence est obligatoire.";
      }
      if (!this.form.customer_reference) {
        errors.customer_reference = "Veuillez sélectionner un client.";
      }
      if (!this.form.date) {
        errors.date = "Sélectionnez la date de la vente.";
      }
      if (this.preparedSaleItems.length === 0) {
        errors.items = "Ajoutez au moins un article avant d'enregistrer.";
      }
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },
    buildSalePayload() {
      const payload = {
        reference: this.form.reference,
        customer_reference: this.form.customer_reference,
        date: this.form.date,
        total_amount: this.totalAmount,
        remark: this.form.remark,
      };
      return payload;
    },
    async saveSale() {
      if (!this.validateForm() || this.submitting) return;
      this.submitting = true;
      try {
        const payload = this.buildSalePayload();
        await this.$store.dispatch("sale/update", {
          reference: this.reference,
          data: payload,
        });
        this.$toast?.success?.("Vente mise à jour avec succès.");
        this.$router.push({
          name: "sales-show",
          params: { reference: this.reference },
        });
      } catch (error) {
        console.error("Error updating sale", error);
        this.$toast?.error?.("Impossible de mettre à jour la vente.");
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    this.loadInitialData();
  },
};
</script>

<style scoped>
.sale-builder {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.card-block {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.block-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.block-header h2 {
  margin: 0;
  font-size: 1.45rem;
  color: #0f172a;
}

.block-header p {
  margin: 0.2rem 0 0;
  color: #6b7280;
  font-size: 0.95rem;
}

.block-actions {
  display: flex;
  gap: 0.75rem;
}

.block-subtitle {
  margin: -0.5rem 0 0;
  color: #6b7280;
  font-size: 0.95rem;
}

.product-selection {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.product-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  border: 1px dashed rgba(148, 163, 184, 0.4);
  border-radius: 18px;
  color: #6b7280;
  text-align: center;
}

.product-state i {
  font-size: 2rem;
  color: #2563eb;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
}

.product-card {
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 18px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;
}

.product-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.product-card:not(:disabled):hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
  border-color: rgba(79, 70, 229, 0.6);
}

.product-image-wrapper {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #94a3b8;
  font-size: 2rem;
}

.product-card-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  text-align: center;
}

.product-card-body h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
  text-transform: uppercase;
}

.product-price {
  font-size: 0.85rem;
  font-weight: 600;
  color: #2563eb;
}

.product-stock {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  display: block;
  margin-top: 0.25rem;
}

.product-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1200;
}

.product-modal {
  width: min(520px, 100%);
  max-height: 70vh;
  background: #ffffff;
  border-radius: 26px;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #0f172a;
}

.product-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.75rem 1.75rem 1.25rem;
}

.product-modal-header h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
}

.product-modal-header p {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.stock-info {
  color: #059669;
  font-weight: 600;
}

.btn.icon-only {
  padding: 0.5rem;
  border-radius: 12px;
}

.product-modal-body {
  padding: 0 1.75rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.product-modal-image {
  display: flex;
  justify-content: center;
}

.product-modal-image img {
  width: 140px;
  height: 140px;
  border-radius: 16px;
  object-fit: cover;
  box-shadow: 0 18px 35px rgba(15, 23, 42, 0.38);
}

.quantity-panels {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
}

.quantity-display {
  background: #f8fafc;
  border-radius: 18px;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  border: 1px solid #e2e8f0;
}

.quantity-display-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.quantity-display span {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.btn-reset {
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.btn-reset:hover {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

.btn-reset i {
  font-size: 0.95rem;
}

.quantity-display strong {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  font-family: "Roboto Mono", monospace;
}

.quantity-display.secondary {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.3);
}

.quantity-keyboard {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.btn-keyboard {
  border: none;
  border-radius: 16px;
  padding: 0.85rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #4f46e5, #2563eb);
  box-shadow: 0 12px 22px rgba(79, 70, 229, 0.32);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-keyboard:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 25px rgba(79, 70, 229, 0.36);
}

.btn-keyboard:active {
  transform: translateY(0);
}

.btn-keyboard.danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  box-shadow: 0 12px 22px rgba(239, 68, 68, 0.28);
}

.btn-keyboard.action {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 12px 22px rgba(16, 185, 129, 0.28);
}

.remark-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.remark-group label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
}

.remark-group textarea {
  border-radius: 14px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #0f172a;
  padding: 0.85rem 1rem;
  resize: vertical;
  min-height: 70px;
}

.remark-group textarea:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.product-modal-footer {
  padding: 1.5rem 1.75rem 1.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.product-modal-footer .btn {
  min-width: 130px;
}

.customer-select-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  text-align: left;
  cursor: pointer;
  background: white;
}

.customer-select-btn .placeholder {
  color: #9ca3af;
}

.customer-select-btn i:last-child {
  margin-left: auto;
  color: #6b7280;
  font-size: 0.9rem;
}

.customer-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1200;
}

.customer-modal {
  width: min(600px, 100%);
  max-height: 85vh;
  background: #ffffff;
  border-radius: 26px;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #0f172a;
}

.customer-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.75rem 1.75rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.customer-modal-header h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
}

.customer-modal-header p {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.customer-modal-body {
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  flex: 1;
}

.customer-search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}

.customer-search:focus-within {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.customer-search i {
  color: #94a3b8;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.customer-search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.95rem;
  width: 100%;
  color: #1e293b;
}

.customer-search input::placeholder {
  color: #94a3b8;
}

.customer-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: #94a3b8;
  text-align: center;
}

.customer-state i {
  font-size: 3rem;
  color: #cbd5e1;
}

.customer-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
}

.customer-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
}

.customer-item:hover {
  background: #f8fafc;
  border-color: #3b82f6;
  transform: translateX(4px);
}

.customer-item:active {
  transform: translateX(2px);
}

.customer-item-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.customer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  flex-shrink: 0;
}

.customer-details {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
}

.customer-details h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.customer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: #64748b;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.meta-item i {
  font-size: 0.9rem;
  color: #94a3b8;
}

.customer-item i:last-child {
  color: #cbd5e1;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.form-group label {
  font-weight: 600;
  color: #475569;
}

.form-control {
  border-radius: 12px;
  border: 1px solid #d1d5db;
  padding: 0.7rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.items-cards-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.items-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.75rem;
}

.item-card {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 12px;
  padding: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.1);
  border-color: rgba(79, 70, 229, 0.4);
}

.item-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
}

.item-card-title {
  flex: 1;
  min-width: 0;
}

.item-card-title h4 {
  margin: 0 0 0.15rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.2;
}

.item-reference {
  display: block;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.15rem;
}

.item-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.item-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.item-label {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
}

.item-value {
  font-weight: 600;
}

.item-remark {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  padding: 0.5rem;
  background: rgba(148, 163, 184, 0.08);
  border-radius: 8px;
  font-size: 0.8rem;
  color: #475569;
  margin-top: 0.15rem;
}

.item-remark i {
  color: #64748b;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.items-total-card {
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.1),
    rgba(79, 70, 229, 0.12)
  );
  border: 2px solid rgba(79, 70, 229, 0.2);
  border-radius: 18px;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

.total-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.total-amount {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  font-family: "Roboto Mono", monospace;
}

.items-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem 1rem;
  color: #6b7280;
  text-align: center;
}

.items-state i {
  font-size: 2rem;
  color: #2563eb;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.badge-light {
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
}

.badge-dark {
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
}

.badge-primary {
  background: rgba(34, 197, 94, 0.16);
  color: #0f766e;
}

.sale-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 1.5rem;
}

.sale-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group.full {
  grid-column: 1 / -1;
}

.summary-card {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.1),
    rgba(79, 70, 229, 0.12)
  );
  color: #1e293b;
}

.summary-card span {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
}

.summary-card strong {
  font-size: 1.25rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-success {
  background: rgba(34, 197, 94, 0.16);
  color: #047857;
  border: none;
}

.btn-success:hover {
  background: rgba(34, 197, 94, 0.24);
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: none;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.btn-sm {
  padding: 0.4rem 0.6rem;
  font-size: 0.8rem;
  min-width: auto;
}

.error {
  color: #ef4444;
  font-size: 0.83rem;
}

.form-error-inline {
  grid-column: 1 / -1;
  margin: -0.25rem 0 0;
}

.has-error {
  border-color: #ef4444;
}

.hint {
  color: #6b7280;
  font-size: 0.85rem;
  text-align: center;
}

.text-danger {
  color: #ef4444;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}

@media (max-width: 992px) {
  .sale-builder {
    padding: 1.5rem 1.5rem 4rem;
  }
  .sale-form {
    grid-template-columns: 1fr;
  }
  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .btn {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .sale-builder {
    padding: 1.25rem 1rem 4rem;
  }
  .card-block {
    padding: 1.5rem;
  }
  .block-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .block-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .items-cards-grid {
    grid-template-columns: 1fr;
  }
  .product-modal {
    border-radius: 20px 20px 0 0;
    max-height: 70vh;
  }
  .product-modal-overlay {
    align-items: flex-end;
    padding: 0;
  }
  .product-modal-body {
    padding: 0 1.5rem 1.5rem;
  }
  .product-modal-footer {
    padding: 1.25rem 1.5rem 1.5rem;
  }
  .customer-modal {
    border-radius: 20px 20px 0 0;
    max-height: 94vh;
  }
  .customer-modal-overlay {
    align-items: flex-end;
    padding: 0;
  }
  .customer-modal-body {
    padding: 0 1.5rem 1.5rem;
  }
  .customer-modal-header {
    padding: 1.5rem 1.5rem 1rem;
  }
  .quantity-display strong {
    font-size: 1.6rem;
  }
  .items-total-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .total-amount {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .card-block {
    padding: 1.25rem;
  }
  .btn {
    width: 100%;
  }
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  .quantity-display {
    padding: 1rem 1.1rem;
  }
  .btn-keyboard {
    padding: 0.75rem;
    font-size: 1.1rem;
  }
}

/* Search Section */
.search-section {
  margin-bottom: 1.5rem;
}

.search-mode-selector {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.search-mode-btn {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-mode-btn:hover {
  border-color: #2563eb;
  background: #f0f4ff;
  color: #2563eb;
}

.search-mode-btn.active {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
}

.search-mode-btn i {
  font-size: 1.1rem;
}

.search-form {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1rem;
}

.search-box {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: #ffffff;
  border: 2px solid #d1d5db;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.search-box:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.search-box i {
  color: #94a3b8;
  font-size: 1.1rem;
}

.search-box input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.95rem;
  color: #1e293b;
  background: transparent;
}

.search-submit-btn {
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;
  white-space: nowrap;
}

.search-submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}

.search-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.search-results-section {
  margin-top: 1rem;
}

.search-results-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.75rem;
}

.search-results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}

.search-result-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.75rem;
  display: flex;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.search-result-card:hover {
  border-color: #2563eb;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
}

.result-image-wrapper {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f7fafc;
}

.result-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.result-info {
  flex: 1;
  min-width: 0;
}

.result-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 0.25rem 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-code {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0 0 0.25rem 0;
}

.result-price {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2563eb;
}

.result-action {
  display: flex;
  align-items: center;
}

.add-item-btn {
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
</style>
